/**
 * Admin > Data Operations > Data Health & Execution Integrity (read-only).
 * Backend: `app.api.v1.admin_execution_integrity`.
 */
import api from "@/services/api";

export type SectionHealthStatus = "HEALTHY" | "ATTENTION" | "UNKNOWN" | "NO_DATA";

export interface SectionHealth {
  status: SectionHealthStatus;
  counts: Record<string, number>;
  total: number;
}

export interface ExecutionIntegritySummary {
  date_from: string;
  date_to: string;
  strategy_key: string | null;
  first_candle: SectionHealth;
  feed_freshness: SectionHealth;
  gate: SectionHealth;
  evidence: SectionHealth;
  reconciliation: SectionHealth;
}

export type IntegrityRow = Record<string, unknown>;

export interface IntegrityRows {
  rows: IntegrityRow[];
  total: number;
}

export type IntegritySection = "first-candle" | "feed-freshness" | "gate" | "evidence" | "reconciliation";

export interface IntegrityParams {
  date_from?: string;
  date_to?: string;
  strategy_key?: string;
  outcome?: string;
  mode?: string;
  severity?: string;
  state?: string;
}

function clean(params: IntegrityParams): Record<string, string> {
  const out: Record<string, string> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") out[k] = String(v);
  });
  return out;
}

const BASE = "/admin/execution-integrity";

export const executionIntegrityService = {
  async getSummary(params: IntegrityParams): Promise<ExecutionIntegritySummary> {
    const { data } = await api.get<ExecutionIntegritySummary>(`${BASE}/summary`, { params: clean(params) });
    return data;
  },

  async getRows(section: IntegritySection, params: IntegrityParams): Promise<IntegrityRows> {
    const { data } = await api.get<IntegrityRows>(`${BASE}/${section}`, { params: clean(params) });
    return data;
  },

  async downloadCsv(section: IntegritySection, params: IntegrityParams): Promise<void> {
    const response = await api.get(`${BASE}/${section}`, {
      params: { ...clean(params), format: "csv" },
      responseType: "blob",
    });
    const g = globalThis;
    const url = g.URL.createObjectURL(response.data);
    const link = g.document.createElement("a");
    link.href = url;
    link.download = `${section}_${params.date_from ?? ""}_${params.date_to ?? ""}.csv`;
    g.document.body.appendChild(link);
    link.click();
    g.document.body.removeChild(link);
    g.URL.revokeObjectURL(url);
  },
};

export default executionIntegrityService;
